// KunstPfote: Tiername in der Browser-Session (snippets/pet-name-dialog.liquid)
//
// Nach dem ersten Foto-Upload einer Session fragt ein Dialog einmal nach dem Namen des Tieres.
// Gespeichert wird er als `petName` im Session-Objekt `kunstpfote_uploads` (localStorage):
//   petName fehlt  → noch nie gefragt → Dialog erscheint beim nächsten Upload
//   petName = ''   → „Ohne Namen weiter“ gewählt → nicht erneut fragen
// Neue Session (neues Objekt ohne petName) = neues Tier → es wird wieder gefragt.
// Nach dem Speichern: Event `kunstpfote:pet-name-changed` ({ detail: { name } }).
// Lesen von überall: window.KunstPfotePetName.get() / .phrase(name, handle, motiv)
// Lädt im <head> (layout/theme.liquid), damit personalization-form.js es beim Start schon hat.

(() => {
  const UPLOAD_DATA_KEY = 'kunstpfote_uploads';
  const MAX_LENGTH = 30;

  // Wie das Tier im Motiv heißt: „Fina beim Dinner“ statt „Fina als Dinner“. Schlüssel = Produkt-Handle.
  // Neue Motive hier ergänzen; ohne Eintrag gilt der neutrale Rückfall „Fina, <Motiv>“.
  const PHRASES = {
    'badezeit': '{name} in der Badewanne',
    'bandana': '{name} mit Bandana',
    'berge': '{name} in den Bergen',
    'bleistift-zeichnung': '{name} als Bleistiftzeichnung',
    'blumen': '{name} mit Blumen',
    'blumenfeld': '{name} im Blumenfeld',
    'box': '{name} in der Box',
    'catsmopolitan': '{name} auf dem Titelblatt',
    'dinner': '{name} beim Dinner',
    'engel': '{name} als Engel',
    'friseur': '{name} beim Friseur',
    'graffiti': '{name} als Graffiti',
    'himmel': '{name} im Himmel',
    'hungrig': '{name}, hungrig',
    'klassisch': '{name}, klassisch',
    'pizza': '{name} mit Pizza',
    'pool': '{name} am Pool',
    'regenbogen': '{name} am Regenbogen',
    'relax': '{name} beim Entspannen',
    'retro-kacheln': '{name} vor Retro-Kacheln',
    'strand': '{name} am Strand',
    'toilette': '{name} auf der Toilette',
    'urlaub': '{name} im Urlaub',
  };

  /**
   * Schreibweise des Namens: Leerzeichen bereinigen; komplett klein („fina“, „mister bean“) oder
   * komplett groß („FINA“) → große Anfangsbuchstaben. Bewusste Schreibweisen („McFly“, „von Bülow“) bleiben.
   */
  function formatName(raw) {
    const name = String(raw || '').trim().replace(/\s+/g, ' ').slice(0, MAX_LENGTH);
    if (!name) return '';
    const lower = name.toLocaleLowerCase('de-DE');
    const allLower = name === lower;
    const allUpper = name.length > 2 && name === name.toLocaleUpperCase('de-DE') && name !== lower;
    if (!allLower && !allUpper) return name;
    return lower.replace(/(^|[\s-])(\p{L})/gu, (_, sep, ch) => sep + ch.toLocaleUpperCase('de-DE'));
  }

  /** Produkt-Handle aus einer URL/einem Pfad („/products/berge?variant=…“ → „berge“) */
  function handleFromPath(path) {
    return (String(path || '').match(/\/products\/([^/?#]+)/) || [])[1] || '';
  }

  /** „Fina in den Bergen“ – ohne Namen der Motivname, ohne Eintrag „Fina, <Motiv>“ */
  function phrase(name, handle, motiv) {
    const formatted = formatName(name);
    if (!formatted) return motiv || '';
    const template = PHRASES[decodeURIComponent(handle || '')];
    if (template) return template.replace('{name}', formatted);
    return motiv ? `${formatted}, ${motiv}` : formatted;
  }

  function readSession() {
    try {
      const data = JSON.parse(localStorage.getItem(UPLOAD_DATA_KEY) || 'null');
      return data && data.sessionId ? data : null;
    } catch {
      return null;
    }
  }

  function getPetName() {
    const name = readSession()?.petName;
    return typeof name === 'string' ? formatName(name) : '';
  }

  function savePetName(name) {
    const data = readSession();
    if (!data) return;
    const clean = formatName(name);
    data.petName = clean;
    localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('kunstpfote:pet-name-changed', { detail: { name: clean } }));
  }

  function openDialog() {
    const dialog = document.getElementById('kp-pet-name-dialog');
    if (!dialog || dialog.open) return;
    const form = dialog.querySelector('form');
    const input = dialog.querySelector('input[name="pet_name"]');
    input.value = getPetName();

    const close = (name) => {
      savePetName(name);
      dialog.close();
    };

    form.onsubmit = (event) => {
      event.preventDefault();
      close(input.value);
    };
    dialog.querySelector('[data-kp-pet-skip]').onclick = () => close('');
    // Escape/Schließen ohne Antwort zählt als „Ohne Namen“ – sonst käme der Dialog bei jedem Upload wieder
    dialog.oncancel = () => { if (!getPetName()) savePetName(''); };

    dialog.showModal();
    input.focus();
  }

  window.addEventListener('kunstpfote:photos-uploaded', () => {
    // Das Upload-Modal (kp-upload-modal.js) fragt den Namen selbst ab
    if (window.KunstPfoteUpload?.isOpen?.()) return;
    const data = readSession();
    if (data && !('petName' in data)) openDialog();
  });

  window.KunstPfotePetName = { get: getPetName, set: savePetName, ask: openDialog, format: formatName, phrase, handleFromPath };
})();
